import React, { useState, useEffect, useRef } from 'react';
import { Product, OptionList } from '../../../util/typings';
import getMatchingVariants from './getMatchingVariants';
import { WholeBean, Cafetiere, Filter, Espresso } from '../../svgs/GrindIcons';
import ToolTip from '../SubBuilder/components/SubBuilderToolTip';
import Money from '../../Money';
import Glide from '@glidejs/glide';

interface Props {
  selectedOptions: OptionList;
  product: Product;
  stockMap: Record<string, string>;
  grindOptions: string[];
  onChange: (updatedOptions: OptionList) => void;
}

const Options: React.FunctionComponent<Props> = ({
  selectedOptions,
  product,
  onChange,
  stockMap,
  grindOptions,
}: Props) => {
  const [openToolTipIndex, setOpenToolTipIndex] = useState(-1);

  const glideEl = useRef(null);
  const glideOptions = {
    type: 'slider',
    perView: 3.5,
    rewind: true,
    rewindDuration: 2000,
    perTouch: 1,
    bound: true,
    gap: 12,
    peek: {
      before: 0,
      after: 0,
    },
    breakpoints: {
      '500': {
        perView: 2.5,
        perTouch: false,
        touchRatio: 0.5,
        dragThreshold: 50,
        gap: 12,
        rewind: true,
        rewindDuration: 500,
        bound: true,
        peek: {
          before: 0,
          after: 0,
        },
      },
    },
  };

  useEffect(() => {
    if (
      glideEl &&
      glideEl.current &&
      (window.innerWidth <= 500 || product.type === 'Gift Card')
    ) {
      const slider = new Glide(glideEl.current, glideOptions);
      slider.mount();
    }
  }, [glideEl]);

  const allNull = selectedOptions.every((option) => option === null);

  if (!product || allNull) {
    return <></>;
  }

  const update = (index: number, value: string) => () => {
    const updatedOptions = [...selectedOptions] as OptionList;
    updatedOptions[index] = value;
    return onChange(updatedOptions);
  };

  const iconHandler = (str: string): JSX.Element | string => {
    const label = typeof str === 'string' ? str : '';
    if (str.includes('Whole Bean')) return <WholeBean />;
    if (str.includes('Cafetiere')) return <Cafetiere />;
    if (str.includes('Filter') || str.includes('Aeropress')) return <Filter />;
    if (str.includes('Espresso')) return <Espresso />;

    const normalized = label.toLowerCase();
    if (normalized.indexOf('gift box') !== -1) {
      return (
        <img
          src={(window as any).theme.iconBoxedUrl}
          alt={label}
          width="64"
          height="64"
        />
      );
    }
    if (
      normalized.indexOf('un-boxed') !== -1 ||
      normalized.indexOf('unboxed') !== -1
    ) {
      return (
        <img
          src={(window as any).theme.iconUnboxedUrl}
          alt={label}
          width="64"
          height="64"
        />
      );
    }
    return label;
  };

  const grindHandler = (str: string): string => {
    const grind = grindOptions.find(
      (type) =>
        type.toLowerCase().includes(str.toLowerCase()) ||
        (type.toLowerCase().includes('aeropress') &&
          str.toLowerCase().includes('aeropress')) ||
        (type.toLowerCase().includes('beans') &&
          str.toLowerCase().includes('bean')),
    );
    if (!grind) {
      return null;
    }
    return grind;
  };

  return (
    <div className="quick-add-opts">
      {product.options.map(({ name, values, position }, index) => (
        <div
          className="quick-add-opt-group relative  glide--ltr"
          key={position}
          ref={glideEl}
        >
          <h4
            className={`quick-add-opt-title ${product.options.length === 1 ? 'single-option' : ''} ${name}`}
            style={{ fontWeight: 'bold' }}
          >
            {name}: {selectedOptions[position - 1]}
          </h4>
          <div
            className="glide__track"
            data-glide-el={values.length > 3 ? 'track' : ''}
          >
            <ul className="quick-add-opt-list size-select-wrapper glide__slides">
              {values.map((value, i) => {
                const possibleOptions = [...selectedOptions];

                possibleOptions.splice(position - 1, 1, value);
                const matchingVariants = getMatchingVariants(
                  possibleOptions,
                  product.variants,
                );
                const matchingVariant =
                  matchingVariants.length === 1 ? matchingVariants[0] : null;

                const lowStock = Boolean(
                  matchingVariant &&
                  stockMap &&
                  stockMap[matchingVariant.id] &&
                  stockMap[matchingVariant.id] === 'low',
                );
                // Only grind options have tooltip content; packaging
                // options (e.g. Gift / Un-boxed) return null and must not
                // render an empty/incorrect tooltip.
                const grindTip = grindHandler(value);
                return (
                  <li
                    className={`relative size-selector quick-add-opt-val${
                      selectedOptions[position - 1] === value ? ' selected' : ''
                    }${matchingVariants.length > 0 ? ' valid' : ''}${
                      matchingVariant && !matchingVariant.available
                        ? ' oos'
                        : ''
                    }`}
                    {...(matchingVariants.length > 1 ||
                    (matchingVariant && matchingVariant.available)
                      ? {}
                      : { 'data-unavailable': true })}
                    {...(lowStock ? { 'data-low-stock': true } : {})}
                    key={i}
                  >
                    <input
                      className="visually-hidden"
                      disabled={matchingVariants.length < 1 || !matchingVariant}
                      name={`option-${position}`}
                      id={`option-${position}-value-${i}`}
                      checked={selectedOptions[position - 1] === value}
                      onChange={update(position - 1, value)}
                      type="radio"
                      value={value}
                    />
                    <label
                      className="relative"
                      htmlFor={`option-${position}-value-${i}`}
                    >
                      {iconHandler(value)}
                      <div className="active-background svg-product-icon-box"></div>
                    </label>
                    {(index === 0 && name === 'Size') || name === 'Coffee' ? (
                      <span className="value-price">
                        {product.type === 'Gift Card' ||
                        product.options.length < 2 ? (
                            <Money amount={product.variants[i].price} />
                          ) : i === 0 ? (
                            <Money
                              amount={product.price_min}
                              withCurrency={false}
                            />
                          ) : (
                            <Money
                              amount={product.price_max}
                              withCurrency={false}
                            />
                          )}
                      </span>
                    ) : (
                      <span className="value-price value-text">
                        {value.split(' ')[0]}
                        {grindTip && (
                          <>
                            {' '}
                            <ToolTip
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openToolTipIndex === i) {
                                  setOpenToolTipIndex(-1);
                                  return;
                                }
                                setOpenToolTipIndex(i);
                              }}
                              open={openToolTipIndex === i}
                            >
                              {grindTip}
                            </ToolTip>
                          </>
                        )}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          {(values.length > 3 || product.type === 'Gift Card') && (
            <div
              className={`controls flex align-center gap-small ${product.type === 'Gift Card' ? 'active' : ''}`}
              data-glide-el="controls"
            >
              <span
                className="glide__arrow glide__arrow--left"
                data-glide-dir="<"
                data-desktop-arrow
              >
                <span className="visually-hidden">Previous Slide</span>
              </span>
              <span
                className="glide__arrow glide__arrow--right"
                data-glide-dir=">"
                data-desktop-arrow
              >
                <span className="visually-hidden">Next Slide</span>
              </span>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Options;
